import { useState, useEffect } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  ChartPieSlice, Receipt, Users as UsersIcon, Car, Warning, Gear, SignOut, Percent,
  Wallet, Money, ShoppingCart, BookOpen, Calculator, GearSix, UserGear, FileArrowUp, Article,
  CurrencyCircleDollar, Bank, Trash, ChartBar, List, X,
} from "@phosphor-icons/react";

const GROUPS = [
  {
    label: "Utama",
    items: [
      { to: "/dashboard", key: "dashboard", icon: ChartPieSlice, label: "Dashboard", end: true, testid: "nav-dashboard" },
      { to: "/sales", key: "sales", icon: Receipt, label: "Penjualan", testid: "nav-sales" },
      { to: "/purchases", key: "purchases", icon: ShoppingCart, label: "Pembelian", testid: "nav-purchases" },
      { to: "/payments", key: "payments", icon: Money, label: "Bayar Cicilan", testid: "nav-payments" },
      { to: "/customers", key: "customers", icon: UsersIcon, label: "Pelanggan", testid: "nav-customers" },
      { to: "/cars", key: "cars", icon: Car, label: "Mobil", testid: "nav-cars" },
    ],
  },
  {
    label: "Master",
    items: [
      { to: "/interest-rates", key: "interest-rates", icon: Percent, label: "Bunga", testid: "nav-rates" },
      { to: "/late-categories", key: "late-categories", icon: Warning, label: "Kategori Telat", testid: "nav-cats" },
      { to: "/catalog", key: "catalog", icon: BookOpen, label: "Katalog Mobil", testid: "nav-catalog" },
      { to: "/calculator", key: "calculator", icon: Calculator, label: "Kalkulator", testid: "nav-calc" },
    ],
  },
  {
    label: "Monitoring",
    items: [
      { to: "/overdue", key: "overdue", icon: Warning, label: "Keterlambatan", testid: "nav-overdue" },
    ],
  },
  {
    label: "Laporan",
    items: [
      { to: "/reports", key: "reports", icon: ChartBar, label: "Pusat Laporan", testid: "nav-reports" },
      { to: "/reports/pnl", key: "reports-pnl", icon: ChartBar, label: "Laba / Rugi", testid: "nav-pnl" },
      { to: "/cashbook", key: "cashbook", icon: Bank, label: "Kas Harian (SM/KP)", testid: "nav-cashbook" },
      { to: "/reports/sales", key: "reports-sales", icon: Receipt, label: "Penjualan", testid: "nav-rep-sales" },
      { to: "/reports/purchases", key: "reports-purchases", icon: ShoppingCart, label: "Pembelian", testid: "nav-rep-purchases" },
      { to: "/commission", key: "commission", icon: CurrencyCircleDollar, label: "Komisi Makelar", testid: "nav-commission" },
    ],
  },
  {
    label: "Sistem",
    items: [
      { to: "/import", key: "import", icon: FileArrowUp, label: "Import Excel", testid: "nav-import" },
      { to: "/audit", key: "audit", icon: Article, label: "Audit & Backup", testid: "nav-audit" },
      { to: "/users", key: "users", icon: UserGear, label: "Pengguna", testid: "nav-users" },
      { to: "/settings", key: "settings", icon: GearSix, label: "Pengaturan", testid: "nav-settings" },
      { to: "/reset-data", key: "reset-data", icon: Trash, label: "Reset Data", testid: "nav-reset" },
    ],
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const perms = user?.permissions || [];
  const isAdmin = user?.role === "admin";
  const hasAccess = (k) => isAdmin || perms.includes(k);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const visibleGroups = GROUPS
    .map((g) => ({ ...g, items: g.items.filter((i) => hasAccess(i.key)) }))
    .filter((g) => g.items.length > 0);

  const Sidebar = (
    <aside className="no-print w-64 shrink-0 bg-[#0A0A0A] text-white flex flex-col h-full">
      <div className="px-6 py-6 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wallet size={22} weight="bold" className="text-white" />
          <div>
            <div className="font-display text-lg font-bold leading-none">SHOWROOM</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-white/50 mt-1">Finance Console</div>
          </div>
        </div>
        <button onClick={() => setDrawerOpen(false)} className="lg:hidden text-white/70 hover:text-white" aria-label="Close menu">
          <X size={20} weight="bold" />
        </button>
      </div>
      <nav className="flex-1 py-2 overflow-y-auto">
        {visibleGroups.map((g) => (
          <div key={g.label} className="py-2">
            <div className="px-6 pt-2 pb-1 text-[9px] tracking-[0.28em] uppercase text-white/35 font-semibold">
              {g.label}
            </div>
            {g.items.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors duration-150 ${
                    isActive ? "bg-white text-[#0A0A0A] font-semibold" : "text-white/70 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <n.icon size={17} weight="regular" />
                {n.label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="border-t border-white/10 p-4">
        <div className="text-xs text-white/60 mb-2">Login sebagai</div>
        <div className="text-sm font-semibold truncate" data-testid="current-user-email">{user?.email}</div>
        <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">{user?.role}</div>
        <button
          onClick={logout}
          data-testid="logout-button"
          className="mt-3 w-full flex items-center justify-center gap-2 py-2 text-xs uppercase tracking-wider border border-white/20 hover:bg-white hover:text-[#0A0A0A] transition-colors duration-150"
        >
          <SignOut size={14} weight="bold" />
          Keluar
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex bg-white">
      {/* Desktop sidebar (>=lg) */}
      <div className="hidden lg:flex">{Sidebar}</div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div className="w-64 shrink-0 h-full">{Sidebar}</div>
          <div className="flex-1 bg-black/60" onClick={() => setDrawerOpen(false)} />
        </div>
      )}

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <div className="lg:hidden no-print flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white sticky top-0 z-30">
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="mobile-menu-btn"
            className="p-2 -ml-2 text-gray-700 hover:bg-gray-100"
            aria-label="Open menu"
          >
            <List size={22} weight="bold" />
          </button>
          <div className="flex items-center gap-2">
            <Wallet size={18} weight="bold" />
            <span className="font-display font-bold text-sm">SHOWROOM</span>
          </div>
          <div className="w-8" />
        </div>
        <Outlet />
      </main>
    </div>
  );
}
